/**
 * Подпись чистой площади комнаты на 2D-чертеже.
 *
 * Рисуется поверх всего, что стоит в комнате, поэтому холст выводит подписи
 * отдельным слоем над мебелью. Где именно стоять, решает `placeRoomLabel` —
 * подпись уходит от люстры и мебели. Само покрытие пола рисует раздел каталога.
 *
 * При отдалении подпись упрощается (`detail`): сначала остаётся одно название комнаты,
 * потом и оно уходит — см. `roomLabelDetail`. Полная подпись при этом остаётся в разметке
 * (скрыта прозрачностью): экспорт в PDF показывает её всегда, а краткую выбрасывает.
 */
import type { PointerEvent } from "react";
import type { Pt } from "@/shared/lib/geometry";
import {
	ROOM_LABEL_FONT_PX,
	ROOM_LABEL_LINE_GAP_PX,
	ROOM_LABEL_PADDING_PX,
} from "../roomLabelPlacement";
import { lodProps, type RoomLabelDetail } from "../levelOfDetail";

interface Props {
	text: string;
	name: string;
	ceilingHeight?: string;
	/** Центр подписи, мм */
	at: Pt;
	/** Размер миллиметра в пикселях — чтобы подпись не «плыла» при зуме */
	mmPerPx: number;
	/** Сколько писать при этом масштабе; по умолчанию — всё */
	detail?: RoomLabelDetail;
	/** Подпись можно нажать (переименовать комнату); без обработчика клики идут сквозь неё */
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

/** Подпись с белой подложкой — читается и поверх мебели. */
export const RoomLabel = ({
	text,
	name,
	ceilingHeight,
	at,
	mmPerPx,
	detail = "full",
	onPointerDown,
}: Props) => {
	const textProps = {
		textAnchor: "middle",
		fill: "#59637a",
	} as const;
	return (
		<g
			style={{
				userSelect: "none",
				pointerEvents: onPointerDown ? "auto" : "none",
				cursor: onPointerDown ? "pointer" : undefined,
			}}
			onPointerDown={onPointerDown}
		>
			<g {...lodProps(detail !== "full")}>
				<FullLabel
					text={text}
					name={name}
					ceilingHeight={ceilingHeight}
					at={at}
					mmPerPx={mmPerPx}
					textProps={textProps}
				/>
			</g>
			{/* Краткая подпись — только на экране: в PDF всегда идёт полная */}
			<g {...lodProps(detail !== "name")} data-plan-export-exclude>
				<rect
					x={
						at.x -
						((name.length * ROOM_LABEL_FONT_PX * 0.5 + ROOM_LABEL_PADDING_PX * 2) * mmPerPx) / 2
					}
					y={at.y - ((ROOM_LABEL_FONT_PX + ROOM_LABEL_PADDING_PX * 2) * mmPerPx) / 2}
					width={(name.length * ROOM_LABEL_FONT_PX * 0.5 + ROOM_LABEL_PADDING_PX * 2) * mmPerPx}
					height={(ROOM_LABEL_FONT_PX + ROOM_LABEL_PADDING_PX * 2) * mmPerPx}
					rx={4 * mmPerPx}
					fill="#ffffff"
					stroke="#d7dce5"
					strokeWidth={mmPerPx}
				/>
				<text
					x={at.x}
					y={at.y}
					dominantBaseline="central"
					fontSize={ROOM_LABEL_FONT_PX * 0.9 * mmPerPx}
					{...textProps}
				>
					{name}
				</text>
			</g>
		</g>
	);
};

function FullLabel({
	text,
	name,
	ceilingHeight,
	at,
	mmPerPx,
	textProps,
}: {
	text: string;
	name: string;
	ceilingHeight?: string;
	at: Pt;
	mmPerPx: number;
	textProps: Record<string, string | number>;
}) {
	const padding = ROOM_LABEL_PADDING_PX * mmPerPx;
	const gap = ROOM_LABEL_LINE_GAP_PX * mmPerPx;
	const lines: { value: string; fontSize: number }[] = [
		{ value: `S=${text}`, fontSize: ROOM_LABEL_FONT_PX * mmPerPx },
	];
	if (ceilingHeight) lines.push({ value: ceilingHeight, fontSize: ROOM_LABEL_FONT_PX * mmPerPx });
	lines.push({ value: name, fontSize: ROOM_LABEL_FONT_PX * 0.8 * mmPerPx });
	const totalHeight =
		lines.reduce((height, line) => height + line.fontSize * 1.2 + padding * 2, 0) +
		gap * (lines.length - 1);
	let top = at.y - totalHeight / 2;
	return (
		<g>
			{lines.map((line, index) => {
				const height = line.fontSize * 1.2 + padding * 2;
				const y = top;
				top += height + gap;
				const width = line.value.length * line.fontSize * 0.5 + padding * 2;
				return (
					<g key={`${line.value}-${index}`}>
						<rect
							x={at.x - width / 2}
							y={y}
							width={width}
							height={height}
							rx={4 * mmPerPx}
							fill="#ffffff"
							stroke="#d7dce5"
							strokeWidth={mmPerPx}
						/>
						<text
							x={at.x}
							y={y + padding + line.fontSize * 0.85}
							fontSize={line.fontSize}
							{...textProps}
						>
							{line.value}
						</text>
					</g>
				);
			})}
		</g>
	);
}
